import cx from 'classnames';
import { Row, Col } from 'antd';
import { cloneDeep } from 'lodash';
import { useDispatch, useSelector } from 'react-redux';
import { useTranslation } from 'react-i18next';
import { isDesktop } from 'react-device-detect';
import { CheckboxChangeEvent } from 'antd/lib/checkbox';

import { Percentile } from './interfaces';
import { THEME } from '../../config/commons';
import { Store } from '../../state/store.interface';
import Icon from '../../common/components/Icon/Icon';
import { LrvText } from '../../common/components/LrvText/LrvText';
import { CloseIcon } from '../../common/components/Icon/CloseIcon';
import { LrvCheckbox } from '../../common/components/LrvCheckbox/LrvCheckbox';
import { IndeterminateCircleIcon } from '../../common/components/Icon/IndeterminateCircleIcon';

import * as sampleSlice from './sampleSlice';
import styles from './QuantityAnimals.module.scss';
import { countPointsRemoved, getPointsUnremoved } from './math-percentile';

interface Props {
  listPercentile: Percentile[];
  labelsUnit: string;
  showAnimalsRemoved: boolean;
  changeListPercentile: (percentiles: Percentile[]) => void;
  showQuantityAnimalsPanel: boolean;
}

export default function QuantityAnimals (props: Props) {
  const { listPercentile, labelsUnit, showAnimalsRemoved, changeListPercentile, showQuantityAnimalsPanel } = props;

  const [t] = useTranslation();
  const dispatch = useDispatch();

  const { theme } = useSelector((state: Store) => state.detail);
  const isLightTheme = theme === THEME.LIGHT;

  const changeActivePercentile = (index: number) => {
    const percentiles: Percentile[] = cloneDeep(listPercentile);
    percentiles[index].isActive = !percentiles[index].isActive;
    changeListPercentile(percentiles);
  };

  const renderPercentile = (props: {percentile: Percentile; index: number;}) => {
    const { percentile, index } = props;

    return (
      <Row key={index} gutter={24} className={styles.toolRow}>
        <Col span={19} className={styles.col}>
          <LrvCheckbox
            id={`percentile-${index}`}
            onChange={() => changeActivePercentile(index)}
            theme={theme}
            checked={percentile.isActive}
            className={styles.toolCheckbox}
          >
            <div className={styles.colorBox} style={{ background: `${percentile.color}` }} />
            <span className={styles.label}>{percentile.rank} {labelsUnit}</span>
          </LrvCheckbox>
        </Col>

        <Col span={5} className={styles.col}>
          <LrvText className={styles.value} theme={theme}>{getPointsUnremoved(percentile.animals).toString()}</LrvText>
        </Col>
      </Row>
    );
  };

  const renderAnimalsRemoved = (props: {count: number; key: string; label: string; iconContainerClassname?: string; checked?: boolean | undefined; onChange: (e: CheckboxChangeEvent) => void; icon: JSX.Element}) => {
    const { count, key, label, iconContainerClassname, checked, onChange, icon } = props;

    return (
      <Row key={key} gutter={24} className={styles.toolRow}>
        <Col span={19} className={styles.col}>
          <LrvCheckbox
            id={`percentile-${key}`}
            onChange={onChange}
            checked={checked}
            className={styles.toolCheckbox}
            theme={theme}
          >
            <div className={cx(styles.colorBox, iconContainerClassname)}>
              {icon}
            </div>

            <span className={styles.label}>{label} </span>
          </LrvCheckbox>
        </Col>

        <Col span={5} className={styles.col}>
          <LrvText className={styles.value} theme={theme}>{count}</LrvText>
        </Col>
      </Row>
    );
  };

  const renderCheckboxPercentile = () => {
    const percentiles = listPercentile.map((percentile, index) => {
      if (percentile.sieved) {
        return renderAnimalsRemoved({
          count: getPointsUnremoved(percentile?.animals || []),
          key: 'sieved-animal',
          label: t('detail.sievedAnimals'),
          iconContainerClassname: styles.sievedAnimals,
          icon: <IndeterminateCircleIcon width={24} height={24} fill={isLightTheme ? '#333333' : 'white'} />,
          checked: percentile.isActive,
          onChange: () => changeActivePercentile(index)
        });
      }

      return renderPercentile({ percentile, index });
    });

    percentiles.unshift(
      <div key='header-quantity-animals' className={styles.header}>
        <div className={styles.group}>
          <div
            className={styles.iconContainer}
            onClick={() => {
              dispatch(sampleSlice.setShowQuantityAnimalsPanel(false));
            }}
          >
            {!isDesktop && <Icon name='close' />}
          </div>
          <div className={cx(styles.label, isLightTheme ? styles.labelLight : styles.labelDark)}>{t('detail.group')}</div>
        </div>

        <div className={cx(styles.label, isLightTheme ? styles.labelLight : styles.labelDark)}>{t('detail.quantity')}</div>
      </div>
    );

    percentiles.push(renderAnimalsRemoved({
      key: 'removed-animals',
      label: t('detail.animalsRemoved'),
      icon: <CloseIcon width={38} height={38} fill={isLightTheme ? '#333333' : 'white'} />,
      checked: showAnimalsRemoved,
      onChange: (event) => dispatch(sampleSlice.setShowAnimalsRemoved(event.target.checked)),
      count: countPointsRemoved(listPercentile),
    }));

    return percentiles;
  };

  if (!showQuantityAnimalsPanel) {
    return null;
  }

  return (
    <div className={cx(styles.tools, isLightTheme ? styles.toolsLight : styles.toolsDark)}>
      {renderCheckboxPercentile()}
    </div>
  );
}
